import React, { useState, useEffect, useCallback, useMemo } from 'react'

type CountdownTimerProps = {
  targetTime: Date
  timezone?: string
  onTimeUp: () => void
  className?: string
}

type TimeRemaining = {
  hours: number
  minutes: number
  seconds: number
  totalSeconds: number
  isExpired: boolean
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetTime,
  timezone,
  onTimeUp,
  className = '',
}) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalSeconds: 0,
    isExpired: false,
  })
  const [hasCalledTimeUp, setHasCalledTimeUp] = useState(false)

  const calculateTimeRemaining = useCallback((): TimeRemaining => {
    const now = new Date()
    const target = new Date(targetTime)
    const diffMs = target.getTime() - now.getTime()

    if (diffMs <= 0) {
      return {
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
        isExpired: true,
      }
    }

    const totalSeconds = Math.floor(diffMs / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    return {
      hours,
      minutes,
      seconds,
      totalSeconds,
      isExpired: false,
    }
  }, [targetTime])

  // Update countdown every second
  useEffect(() => {
    // Initial calculation
    setTimeRemaining(calculateTimeRemaining())

    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining()
      setTimeRemaining(remaining)

      // Call onTimeUp when expired (only once)
      if (remaining.isExpired && !hasCalledTimeUp) {
        setHasCalledTimeUp(true)
        onTimeUp()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [calculateTimeRemaining, onTimeUp, hasCalledTimeUp])

  // Format the target time for display
  const formattedTargetTime = useMemo(() => {
    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }

    if (timezone) {
      options.timeZone = timezone
    }

    try {
      return new Date(targetTime).toLocaleTimeString('en-US', options)
    } catch {
      return new Date(targetTime).toLocaleTimeString('en-US')
    }
  }, [targetTime, timezone])

  // Render countdown display
  const renderCountdown = () => {
    if (timeRemaining.isExpired) {
      return (
        <div className="text-center">
          <div className="text-3xl font-bold text-green-600 mb-1">Starting now...</div>
          <div className="flex items-center justify-center gap-2 text-sm text-green-600">
            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Launching simulation</span>
          </div>
        </div>
      )
    }

    const parts: string[] = []

    if (timeRemaining.hours > 0) {
      parts.push(`${timeRemaining.hours}h`)
    }
    if (timeRemaining.minutes > 0 || timeRemaining.hours > 0) {
      parts.push(`${timeRemaining.minutes}m`)
    }
    parts.push(`${timeRemaining.seconds}s`)

    return (
      <div className="text-center">
        <div className="text-sm text-gray-500 mb-2">Starting automatically in</div>
        <div className="text-4xl font-bold text-blue-600 tracking-tight">
          {parts.join(' ')}
        </div>
        <div className="text-xs text-gray-400 mt-2">
          Scheduled: {formattedTargetTime}
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

      {renderCountdown()}

      {/* Progress indicator (visual representation of time) */}
      {!timeRemaining.isExpired && timeRemaining.totalSeconds < 3600 && (
        <div className="mt-4">
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-1000 ease-linear"
              style={{
                width: `${Math.min(100, (1 - timeRemaining.totalSeconds / 3600) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default CountdownTimer
